import { useSize } from 'ahooks';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, FocusEvent } from 'react';
import defaultTo from 'lodash/defaultTo';
import { isRTL } from '@umu/umu-util';
import useSticky from './useSticky';
import isNumber from 'lodash/isNumber';
import { SCREEN_STATE } from '../types';

export interface UsePdfToolbarOptions {
    currentPage: number;
    totalPages: number;
    screenState: SCREEN_STATE;
    showOutlines: boolean;
    onPageJump: (page: number) => void;
    /**
     * 工具栏是否可见（display != none 的语义）
     * 用于避免在隐藏态测量到 width=0 并写入 state，导致下次显示宽度错误。
     */
    isToolbarVisible: boolean;
}

export interface UsePdfToolbarReturn {
    // Refs
    toolbarRef: React.RefObject<HTMLDivElement>;
    // States
    isSticky: boolean;
    currentPageText: string;
    isInputting: boolean;
    toolbarLeft: number;
    toolbarWidth: number | string;
    toolbarHeight: number;
    // Handlers
    onInputFocus: (e: FocusEvent<HTMLInputElement>) => void;
    onInputBlur: (e: FocusEvent<HTMLInputElement>) => void;
    onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onInputPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
    onPageInputClick: () => void;
    // Computed
    toolbarStyle: React.CSSProperties;
}

/**
 * PdfToolbar 业务逻辑 Hook
 * 负责处理页面输入、工具栏位置计算、状态管理等业务逻辑
 */
export function usePdfToolbar(options: UsePdfToolbarOptions): UsePdfToolbarReturn {
    const {
        currentPage: displayPageNum,
        totalPages,
        screenState,
        showOutlines,
        onPageJump,
        isToolbarVisible,
    } = options;

    const toolbarRef = useRef<HTMLDivElement>(null);
    // 父元素和滚动容器的 ref，挂载后由 useLayoutEffect 赋值，
    // 在 useSize 的 useEffect 订阅前完成（useLayoutEffect 先于 useEffect 执行）
    const parentRef = useRef<HTMLElement | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const [currentPageText, setCurrentPageText] = useState<string>(' - ');
    const [isInputting, setIsInputting] = useState<boolean>(false);
    const [toolbarLeft, setToolbarLeft] = useState<number>(0);
    const [toolbarHeight, setToolbarHeight] = useState<number>(0);

    const isFullScreen = screenState === 'full';
    // 全屏模式下强制保持 sticky（position:fixed）：
    // 缩放时水平溢出触发 doc-container.full 的水平滚动条 → 可视高度缩小 →
    // IntersectionObserver 误判 sentinel 可见 → isSticky 翻转为 false → 工具栏消失
    const isSticky = useSticky(toolbarRef, isFullScreen && isToolbarVisible);
    const toolbarSize = useSize(toolbarRef);

    // 挂载后初始化父元素和滚动容器引用
    // toolbarParent = .umu-pdf-version-toolbar 的父元素
    // scrollContainer = .doc-container.full（全屏滚动容器，向上 2 层）
    useLayoutEffect(() => {
        parentRef.current = toolbarRef.current?.parentElement ?? null;
        scrollContainerRef.current =
            toolbarRef.current?.parentElement?.parentElement?.parentElement ?? null;
    }, []);

    // 直接观察父元素和滚动容器的尺寸变化（ResizeObserver 驱动），
    // 替代原来通过 useSize(<html>) 间接感知——侧边栏展开/收起等不改变 <html> 宽度的情况也能正确响应
    const toolbarParentSize = useSize(parentRef);
    const scrollContainerSize = useSize(scrollContainerRef);

    /**
     * toolbarWidth 由 useSize 直接派生，始终与 DOM 同步，无需独立 state。
     *
     * 全屏：取滚动容器宽度（.doc-container.full），避免 toolbarParent.x 因水平滚动变负。
     * 非全屏 sticky：取父元素宽度，保证 fixed 后与原位等宽。
     * 非全屏非 sticky：'auto'，跟随文档流。
     */
    const toolbarWidth = useMemo((): number | string => {
        if (!isToolbarVisible) return 0;
        if (isFullScreen) {
            return scrollContainerSize?.width ?? toolbarParentSize?.width ?? 0;
        }
        return isSticky ? (toolbarParentSize?.width ?? 0) : 'auto';
    }, [isFullScreen, isSticky, isToolbarVisible, scrollContainerSize?.width, toolbarParentSize?.width]);

    // toolbarLeft（position: fixed 时的 left/right 锚点）在父元素或滚动容器尺寸变化时同步读取
    // isSticky 加入依赖：切换为 fixed 时立即用最新视口坐标定位，
    // 避免页面存在水平滚动时 toolbarLeft 过期导致工具栏偏右
    useLayoutEffect(() => {
        if (!isToolbarVisible) return;
        if (isFullScreen) {
            // 使用滚动容器的 x，不受容器内水平滚动影响
            const containerRect = scrollContainerRef.current?.getBoundingClientRect();
            const parentRect = parentRef.current?.getBoundingClientRect();
            setToolbarLeft(containerRect?.x ?? parentRect?.x ?? 0);
        } else {
            setToolbarLeft(parentRef.current?.getBoundingClientRect()?.x ?? 0);
        }
    }, [isFullScreen, isToolbarVisible, screenState, showOutlines, toolbarParentSize?.width, scrollContainerSize?.width, isSticky]);

    // 工具栏高度：使用 ahooks/useSize（内部基于 ResizeObserver），避免手写监听引入兼容问题
    useEffect(() => {
        if (!isToolbarVisible) {
            return;
        }
        const h = toolbarSize?.height;
        if (isNumber(h) && h > 0) {
            setToolbarHeight(h);
        }
    }, [isToolbarVisible, toolbarSize?.height]);

    /**
     * 输入框聚焦处理
     */
    const onInputFocus = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            e.target.value = String(displayPageNum);
        },
        [displayPageNum],
    );

    /**
     * 输入框失焦处理
     */
    const onInputBlur = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
            setIsInputting(false);
            const rawValue = Number(e.target.value);
            const nextPage = defaultTo(Number.isNaN(rawValue) ? undefined : Math.trunc(rawValue), displayPageNum);

            // 如果页码没有变化，不触发跳转
            if (displayPageNum === nextPage) {
                return;
            }

            onPageJump(nextPage);
        },
        [displayPageNum, onPageJump],
    );

    /**
     * 输入框键盘事件处理
     */
    const onInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const key = e.key;

            // 禁止输入小数点、指数符号和正负号
            if (key === '.' || e.keyCode === 190 || key === 'e' || key === 'E' || key === '+' || key === '-') {
                e.preventDefault();
                return;
            }

            if (key === 'Enter') {
                setIsInputting(false);
                e.preventDefault();
                e.stopPropagation();
                const rawValue = parseInt(e.currentTarget.value, 10);
                const nextPage = defaultTo(Number.isNaN(rawValue) ? undefined : Math.trunc(rawValue), displayPageNum);

                // 如果页码没有变化，不触发跳转
                if (displayPageNum === nextPage) {
                    return;
                }

                onPageJump(nextPage);
            }
        },
        [displayPageNum, onPageJump],
    );

    /**
     * 输入框粘贴事件处理
     */
    const onInputPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
        const text = e.clipboardData.getData('text');
        if (!/^\d+$/.test(text)) {
            e.preventDefault();
        }
    }, []);

    /**
     * 页面输入框点击处理
     */
    const onPageInputClick = useCallback(() => {
        setIsInputting(true);
    }, []);

    /**
     * 更新当前页面文本
     */
    useEffect(() => {
        if (!displayPageNum || !totalPages) {
            return;
        }
        setCurrentPageText(`${displayPageNum} / ${totalPages}`);
    }, [displayPageNum, totalPages]);

    /**
     * 计算工具栏样式
     */
    const toolbarStyle: React.CSSProperties = !isRTL()
        ? { left: toolbarLeft, width: toolbarWidth }
        : { right: toolbarLeft, width: toolbarWidth };

    return {
        isSticky,
        toolbarRef,
        currentPageText,
        isInputting,
        toolbarLeft,
        toolbarWidth,
        toolbarHeight,
        onInputFocus,
        onInputBlur,
        onInputKeyDown,
        onInputPaste,
        onPageInputClick,
        toolbarStyle,
    };
}
